import { Point, Rectangle } from '@awayjs/core';
import { ContextGLDrawMode } from '../base/ContextGLDrawMode';
import { ContextGLCompareMode } from '../base/ContextGLCompareMode';
import { ContextGLVertexBufferFormat } from '../base/ContextGLVertexBufferFormat';
import { Image2D, ImageSampler } from '../image/';
import { ContextGLClearMask } from '../base/ContextGLClearMask';
import { ContextGLTriangleFace } from '../base/ContextGLTriangleFace';
import { FilterUtils } from '../utils/FilterUtils';
import { DisplacementFilter, BlurFilter, BevelFilter, ThresholdFilter, ColorMatrixFilter, DropShadowFilter } from '../filters';
import { CopyFilterInstanced } from '../filters/CopyFilterInstanced';
import { Settings } from '../Settings';
import ImageUtils from '../utils/ImageUtils';
var tmpInputRectCopy = new Rectangle();
var tmpOutputRectCopy = new Rectangle();
var tmpOutputRectFilter = new Rectangle();
var tmpZERO = Object.freeze(new Point(0, 0));
var FilterManager = /** @class */ (function () {
    function FilterManager(_stage) {
        var _a;
        this._stage = _stage;
        this._texturePool = {};
        this._filterCache = {};
        this._filterConstructors = (_a = {},
            _a[BevelFilter.filterName] = BevelFilter,
            _a[BlurFilter.filterName] = BlurFilter,
            _a[DisplacementFilter.filterName] = DisplacementFilter,
            _a[ColorMatrixFilter.filterName] = ColorMatrixFilter,
            _a[DropShadowFilter.filterName] = DropShadowFilter,
            // Glow has same implementation, but not has offsets. Drop shadow and glow - same filters
            _a[DropShadowFilter.filterNameAlt /*glow */] = DropShadowFilter,
            _a);
        FilterManager._instance = this;
        if (Settings.FILTERS_USE_INSTANCED_COPY) {
            this._copyFilterInstanced = new CopyFilterInstanced(this);
        }
    }
    Object.defineProperty(FilterManager, "MAX_TMP_TEXTURE", {
        get: function () {
            return ImageUtils.MAX_SIZE;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FilterManager, "instance", {
        get: function () {
            return this._instance;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FilterManager.prototype, "context", {
        get: function () {
            return this._stage.context;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(FilterManager.prototype, "stage", {
        get: function () {
            return this._stage;
        },
        enumerable: false,
        configurable: true
    });
    FilterManager.prototype.popTemp = function (_width, _height, msaa) {
        if (msaa === void 0) { msaa = false; }
        var width = Math.max(FilterManager.MIN_TMP_TEXTURE, 2 << Math.log2(_width - 1));
        var height = Math.max(FilterManager.MIN_TMP_TEXTURE, 2 << Math.log2(_height - 1));
        if (width > FilterManager.MAX_TMP_TEXTURE || height > FilterManager.MAX_TMP_TEXTURE) {
            // eslint-disable-next-line max-len
            console.warn("[Filter manager] Temporary texture size " + width + "x" + height + " for " + _width + "x" + _height + " is bigger that limit, clamp to " + FilterManager.MAX_TMP_TEXTURE);
            width = Math.min(width, FilterManager.MAX_TMP_TEXTURE);
            height = Math.min(height, FilterManager.MAX_TMP_TEXTURE);
        }
        msaa = msaa && this.context.glVersion === 2;
        var key = width + "_" + height + "_" + ~~msaa;
        var image;
        if (!this._texturePool[key]) {
            this._texturePool[key] = [];
        }
        else {
            image = this._texturePool[key].pop();
        }
        if (!image) {
            // TODO
            // we have problems with cyclic imports
            // and can't create TmpImage2D class, use a regular Image2D with mixin
            image = (new Image2D(width, height, true));
            image.poolKey = key;
            image.antialiasQuality = msaa ? 8 : 0;
        }
        return image;
    };
    FilterManager.prototype.pushTemp = function (image) {
        var im = image;
        if (im && im.poolKey) {
            this._texturePool[im.poolKey].push(im);
        }
    };
    FilterManager.prototype._initFilterElements = function () {
        if (this._filterVertexBuffer) {
            return;
        }
        this._filterVertexBuffer = this.context.createVertexBuffer(6, 8);
        this._filterVertexBuffer.uploadFromArray(new Float32Array([
            0, 0,
            1, 1,
            1, 0,
            0, 0,
            0, 1,
            1, 1
        ]), 0, 6);
        this._filterSampler = new ImageSampler(false, false, false);
    };
    FilterManager.prototype._bindFilterElements = function () {
        var ctx = this.context;
        if (!this._filterVAO) {
            // if there are not VAO, but support - create it and bind for attach attributes
            var vao = ctx.hasVao ? ctx.createVao() : null;
            vao && vao.bind();
            ctx.setVertexBufferAt(0, this._filterVertexBuffer, 0, ContextGLVertexBufferFormat.FLOAT_2);
            this._filterVAO = vao;
        }
        else {
            // already init, bind it
            this._filterVAO.bind();
        }
    };
    FilterManager.prototype._unbindFilterElements = function () {
        // to be sure that VAO wasn't corrupted by next shader
        this._filterVAO && this._filterVAO.unbind(true);
    };
    FilterManager.prototype.getFilter = function (name, props) {
        if (!this._filterCache[name] && !this._filterConstructors[name]) {
            console.warn('[FilterManager] Filter not implemented:', name);
            return null;
        }
        var filter = this._filterCache[name];
        if (filter) {
            filter.applyProps(props);
            return filter;
        }
        return this._filterCache[name] = new this._filterConstructors[name](props);
    };
    FilterManager.prototype.applyFilter = function (source, target, sourceRect, destRectOrPoint, _filterNameOrProps, // legacy
    _options, _clearOutput) {
        if (_clearOutput === void 0) { _clearOutput = false; }
        var options = typeof _filterNameOrProps === 'string' ? _options : _filterNameOrProps;
        if (!options || !options.filterName) {
            return false;
        }
        var name = options.filterName;
        var filter = this.getFilter(name, options);
        if (!filter || !filter.isValid) {
            return false;
        }
        this.renderFilter(source, target, sourceRect || source.rect, destRectOrPoint || source.rect, filter);
        return true;
    };
    FilterManager.prototype.computeFiltersPadding = function (bounds, options, target) {
        if (target === void 0) { target = bounds.clone(); }
        for (var _i = 0, options_1 = options; _i < options_1.length; _i++) {
            var prop = options_1[_i];
            // can be NULL for unimplemented filters
            if (!prop) {
                continue;
            }
            var filter = this.getFilter(prop.filterName, prop);
            if (!filter || !filter.isValid) {
                continue;
            }
            FilterUtils.nonAlocUnion(target, filter.meashurePad(bounds, tmpInputRectCopy), target);
        }
        return target;
    };
    FilterManager.prototype.applyFilters = function (source, target, sourceRect, destRectOrPoint, options) {
        var _a;
        var _this = this;
        var opts = options.filter(function (e) { return e && _this._filterConstructors[e.filterName]; });
        if (opts.length === 0)
            return false;
        if (opts.length === 1) {
            var filter = this.getFilter(opts[0].filterName, opts[0]);
            if (!filter.isValid) {
                return false;
            }
            this.renderFilter(source, target, sourceRect || source.rect, destRectOrPoint || source.rect, filter, true);
            return true;
        }
        var flip = this.popTemp(target.width, target.height);
        var flop = this.popTemp(target.width, target.height);
        for (var i = 0; i < opts.length; i++) {
            var filter = this.getFilter(opts[i].filterName, opts[i]);
            if (!filter.isValid) {
                return false;
            }
            var inputImage = i === 0 ? source : flip;
            var outputImage = (i === opts.length - 1) ? target : flop;
            this.renderFilter(inputImage, outputImage, sourceRect || source.rect, destRectOrPoint || source.rect, filter, i < (opts.length - 1));
            _a = [flop, flip], flip = _a[0], flop = _a[1];
        }
        this.pushTemp(flip);
        this.pushTemp(flop);
        return true;
    };
    FilterManager.prototype.renderFilter = function (source, target, inputRect, outputRect, filter, clearOutput) {
        if (clearOutput === void 0) { clearOutput = false; }
        this._initFilterElements();
        var stage = this._stage;
        var context = this.context;
        var outRect = tmpOutputRectFilter;
        if (outputRect instanceof Point) {
            outRect.setTo(outputRect.x, outputRect.y, inputRect.width, inputRect.height);
            // todo there are bug with this on SceneImage2D for Bevel filter,
            //  but ok for other, check this and fix
            // filter.meashurePad(outRect, outRect);
        }
        else {
            outRect.copyFrom(outputRect);
        }
        var renderToSelf = source === target;
        // tmp texture, avoid framebuffer loop
        var output = target;
        if (renderToSelf) {
            // we render to tmp, not require use offset
            output = this.popTemp(outRect.width, outRect.height);
            outRect.x = 0;
            outRect.y = 0;
        }
        filter.setRenderState(source, output, inputRect, outRect, this);
        // context will enable blend when it was changed
        context.setBlendFactors(filter.blendSrc, filter.blendDst);
        context.setBlendState(filter.requireBlend);
        context.setCulling(ContextGLTriangleFace.NONE);
        var isFirstTask = true;
        var task;
        // iterate while filter have tasks
        while ((task = filter.nextTask())) {
            task.preActivate(stage);
            stage.setRenderTarget(task.target, false, 0, 0, true);
            stage.setScissor(task.clipRect);
            // because we use TMP image, need clear it
            // but this is needed only when a blend composer is required, when a copy filter used
            // or when required by filter chain, clear output for end task
            if (renderToSelf && filter.requireBlend || task.needClear || clearOutput && !filter.hasNextTask()) {
                context.clear(0, 0, 0, 0, 0, 0, ContextGLClearMask.ALL);
            }
            context.setProgram(task.getProgram(stage));
            context.setDepthTest(false, ContextGLCompareMode.LESS_EQUAL);
            // bind filter elements for first pass after set program
            if (isFirstTask) {
                this._bindFilterElements();
                isFirstTask = false;
            }
            task.activate(stage, null, null);
            context.drawVertices(ContextGLDrawMode.TRIANGLES, 0, 6);
            // maybe MSAA, present is required
            context._texContext._currentRT.present();
            task.deactivate(stage);
        }
        if (renderToSelf) {
            // copy output to target texture
            this.copyPixels(output, target, outRect, outputRect, false);
            this.pushTemp(output);
        }
        filter.clear(this);
        stage.setScissor(null);
        this._unbindFilterElements();
    };
    FilterManager.prototype.copyPixels = function (source, target, rect, destPoint, mergeAlpha, blend) {
        var _this = this;
        if (mergeAlpha === void 0) { mergeAlpha = false; }
        if (blend === void 0) { blend = ''; }
        //early out for values that won't produce any visual update
        if (destPoint.x < -rect.width
            || destPoint.x > target.width
            || destPoint.y < -rect.height
            || destPoint.y > target.height) {
            return;
        }
        var inputRect = tmpInputRectCopy;
        inputRect.copyFrom(rect);
        var outputRect = tmpOutputRectCopy;
        outputRect.setTo(destPoint.x, destPoint.y, rect.width, rect.height);
        // clamp input rect above source size
        // we can't use data outside
        if (inputRect.x < 0) {
            inputRect.width -= -inputRect.x;
            outputRect.x += -inputRect.x;
            inputRect.x = 0;
        }
        if (inputRect.y < 0) {
            inputRect.height -= -inputRect.y;
            outputRect.y += -inputRect.y;
            inputRect.y = 0;
        }
        if (inputRect.right > source.width) {
            var delta = source.width - inputRect.right;
            inputRect.width -= delta;
        }
        if (inputRect.bottom > source.height) {
            var delta = source.height - inputRect.bottom;
            inputRect.height -= delta;
        }
        if (outputRect.x < 0) {
            inputRect.x += -outputRect.x;
            inputRect.width -= -outputRect.x;
            outputRect.x = 0;
        }
        if (outputRect.y < 0) {
            inputRect.y += -outputRect.y;
            inputRect.height -= -outputRect.y;
            outputRect.y = 0;
        }
        if (inputRect.width > target.width - outputRect.x)
            inputRect.width = target.width - outputRect.x;
        if (inputRect.height > target.height - outputRect.y)
            inputRect.height = target.height - outputRect.y;
        // we should use same size of output, because we should not change rectangle dimension, only offset
        outputRect.width = inputRect.width;
        outputRect.height = inputRect.height;
        // target image has MSAA
        var msaa = this.context.glVersion === 2 && target.antialiasQuality > 0;
        if (this._copyFilterInstanced &&
            (this._copyFilterInstanced.target !== target ||
                this._copyFilterInstanced.target === source)) {
            this.flushDelayedTask('Manually');
        }
        var tmp;
        // copy to TMP, because we can't copy pixels from itself
        if (target === source) {
            tmp = this.popTemp(source.width, source.height);
            this._stage.setRenderTarget(source, false, 0, 0, true);
            this._stage.setScissor(null);
            // TS !== AS3, it use a auto-type inference, not needed to insert it in all places
            var tmpImageAbst = tmp.getAbstraction(this._stage);
            this.context.copyToTexture(tmpImageAbst.getTexture(), source.rect, tmpZERO);
            this._stage.setRenderTarget(tmp, false, 0, 0, true);
        }
        if (mergeAlpha || msaa || blend) {
            var copy = Settings.FILTERS_USE_INSTANCED_COPY
                ? this._copyFilterInstanced
                : (this._copyPixelFilter = this.getFilter(ColorMatrixFilter.filterName));
            if (copy instanceof CopyFilterInstanced && (copy.requireBlend !== mergeAlpha || (copy.target !== target && copy.target))) {
                copy.flush();
            }
            copy.blend = blend;
            copy.requireBlend = mergeAlpha;
            if (copy instanceof CopyFilterInstanced) {
                copy.target = target;
                copy.addCopyTask(inputRect, outputRect, tmp || source);
                this.context.stateChangeCallback = null;
                // because we require copy to self, we can't do this delayed
                if (copy.mustBeFlushed || target === source) {
                    copy.flush();
                }
                else {
                    this.context.stateChangeCallback = function (e) { return _this.flushDelayedTask(e); };
                }
            }
            else {
                this.renderFilter(source, target, inputRect, outputRect, copy);
                copy.requireBlend = true;
            }
        }
        else {
            this.flushDelayedTask('copyPixelByTexture');
            if (!tmp) {
                this._stage.setRenderTarget(source, false, 0, 0, true);
                this._stage.setScissor(null);
            }
            // TS !== AS3, it use a auto-type inference, not needed to insert it in all places
            var targetImageAbst = target.getAbstraction(this._stage);
            this.context.copyToTexture(targetImageAbst.getTexture(), inputRect, outputRect.topLeft);
        }
        if (tmp) {
            this.pushTemp(tmp);
        }
        // reset target
        this._stage.setRenderTarget(null);
    };
    FilterManager.prototype.flushDelayedTask = function (reason) {
        if (!this._copyFilterInstanced || !this._copyFilterInstanced.length) {
            return;
        }
        // important! reset callback before flush, because flush change state
        this.context.stateChangeCallback = null;
        console.debug('[Instanced] Instanced copyPixel was flushed by:', reason, this._copyFilterInstanced.length);
        this._copyFilterInstanced.flush();
    };
    FilterManager.prototype.threshold = function (source, target, rect, destPoint, operation, threshold, color, mask, copySource) {
        //early out for values that won't produce any visual update
        if (destPoint.x < -rect.width
            || destPoint.x > target.width
            || destPoint.y < -rect.height
            || destPoint.y > target.height) {
            return;
        }
        if (!this._thresholdFilter)
            this._thresholdFilter = new ThresholdFilter();
        this._thresholdFilter.operation = operation;
        this._thresholdFilter.threshold = threshold;
        this._thresholdFilter.color = color;
        this._thresholdFilter.mask = mask;
        // if we copy self to self with same location - this means that anyways source pixel will used
        // without this hack need use a more complex implementation
        // eslint-disable-next-line max-len
        this._thresholdFilter.copySource = copySource || (source === target && rect.x === destPoint.x && rect.y === destPoint.y);
        this.renderFilter(source, target, rect, destPoint, this._thresholdFilter);
    };
    FilterManager.prototype.colorTransform = function (source, target, rect, colorTransform) {
        if (!this._copyPixelFilter) {
            this._copyPixelFilter = this.getFilter(ColorMatrixFilter.filterName);
        }
        this._copyPixelFilter.blend = '';
        this._copyPixelFilter.requireBlend = false;
        this._copyPixelFilter.colorTransform = colorTransform;
        this.renderFilter(source, target, rect, rect, this._copyPixelFilter);
        this._copyPixelFilter.colorTransform = null;
    };
    FilterManager.MIN_TMP_TEXTURE = 64;
    return FilterManager;
}());
export { FilterManager };
